(() => {
  const MODE_PARAM = 'purchase_mode';

  const withMode = (href, mode) => {
    if (!href || href === '#') return href;

    try {
      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/products/')) return href;
      url.searchParams.set(MODE_PARAM, mode);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (_error) {
      return href;
    }
  };

  class ProductPurchaseModes extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.modeButtons = Array.from(this.querySelectorAll('[data-purchase-mode-button]'));
      this.modePanels = Array.from(this.querySelectorAll('[data-purchase-mode-panel]'));
      this.modeBreadcrumbs = Array.from(document.querySelectorAll('[data-purchase-mode-breadcrumb]'));
      this.variantSelects = Array.from(this.querySelectorAll('[data-finished-option]'));
      this.optionsContainer = this.querySelector('[data-finished-options]');
      this.variantInput = this.querySelector('[data-finished-variant-id]');
      this.price = this.querySelector('[data-finished-price]');
      this.comparePrice = this.querySelector('[data-finished-compare-price]');
      this.availability = this.querySelector('[data-finished-availability]');
      this.submitButton = this.querySelector('[data-finished-submit]');
      this.submitText = this.submitButton?.querySelector('[data-finished-submit-text]');
      this.quantityInput = this.querySelector('[data-finished-quantity-input]');
      this.quantityMinus = this.querySelector('[data-finished-quantity-minus]');
      this.quantityPlus = this.querySelector('[data-finished-quantity-plus]');
      this.finishedForm = this.querySelector('.product-purchase-modes__form');
      this.kitProductTitle = this.querySelector('[data-kit-product-title]')?.value || document.title;
      this.kitProductUrl = this.querySelector('[data-kit-product-url]')?.value || window.location.pathname;
      this.assemblyService = this.querySelector('[data-assembly-service]');
      this.assemblyKind = this.assemblyService?.dataset.assemblyKind === 'necklace' ? 'necklace' : 'bracelet';
      this.assemblyCheckbox = this.querySelector('[data-assembly-checkbox]');
      this.assemblyDetails = this.querySelector('[data-assembly-details]');
      this.assemblyMeasurement = this.querySelector('[data-assembly-measurement]');
      this.assemblyUnit = this.querySelector('[data-assembly-unit]');
      this.assemblyFit = this.querySelector('[data-assembly-fit]');
      this.assemblyNotes = this.querySelector('[data-assembly-notes]');
      this.assemblyError = this.querySelector('[data-assembly-error]');
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.quantityMaxBeforeAssembly = this.quantityInput?.getAttribute('max') || '';

      const variantData = this.querySelector('[data-finished-variants]');
      try {
        this.variants = JSON.parse(variantData?.textContent || '[]');
      } catch (_error) {
        this.variants = [];
      }
      this.availableVariants = this.variants.filter((variant) => variant.available);
      if (this.optionsContainer) this.optionsContainer.hidden = !this.availableVariants.length;

      this.modeButtons.forEach((button) => {
        button.addEventListener('click', () => this.setMode(button.dataset.purchaseModeButton, true));
        button.addEventListener('keydown', (event) => this.handleTabKeydown(event));
      });

      this.variantSelects.forEach((select, index) => {
        select.addEventListener('change', () => this.updateVariant(true, index));
      });

      this.quantityMinus?.addEventListener('click', () => this.adjustQuantity(-1));
      this.quantityPlus?.addEventListener('click', () => this.adjustQuantity(1));
      this.quantityInput?.addEventListener('change', () => this.normalizeQuantity());
      this.assemblyCheckbox?.addEventListener('change', () => this.updateAssemblyState());
      this.assemblyUnit?.addEventListener('change', () => this.updateMeasurementUnit());
      this.assemblyMeasurement?.addEventListener('input', () => this.clearAssemblyError());
      this.assemblyFit?.addEventListener('change', () => this.clearAssemblyError());
      this.finishedForm?.addEventListener('submit', (event) => this.handleAssemblySubmit(event), true);

      const requestedMode = new URL(window.location.href).searchParams.get(MODE_PARAM);
      const initialMode = requestedMode === 'diy' || requestedMode === 'finished'
        ? requestedMode
        : this.dataset.defaultPurchaseMode || 'finished';

      this.setMode(initialMode, false);
      this.updateVariant(false);
      this.updateAssemblyState();
    }

    setMode(mode, updateUrl) {
      const nextMode = mode === 'diy' ? 'diy' : 'finished';
      this.dataset.activePurchaseMode = nextMode;

      this.modeButtons.forEach((button) => {
        const active = button.dataset.purchaseModeButton === nextMode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
      });

      this.modePanels.forEach((panel) => {
        panel.hidden = panel.dataset.purchaseModePanel !== nextMode;
      });

      this.modeBreadcrumbs.forEach((breadcrumb) => {
        breadcrumb.hidden = breadcrumb.dataset.purchaseModeBreadcrumb !== nextMode;
      });

      this.syncHeaderMode(nextMode);

      document.querySelectorAll('[data-product-mode-link], .diy-everything__related a[href*="/products/"]').forEach((link) => {
        link.href = withMode(link.getAttribute('href'), nextMode);
      });

      if (!updateUrl) return;
      const url = new URL(window.location.href);
      url.searchParams.set(MODE_PARAM, nextMode);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    handleTabKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      event.preventDefault();
      const currentIndex = this.modeButtons.indexOf(event.currentTarget);
      let nextIndex = currentIndex;

      if (event.key === 'ArrowLeft') nextIndex = Math.max(0, currentIndex - 1);
      if (event.key === 'ArrowRight') nextIndex = Math.min(this.modeButtons.length - 1, currentIndex + 1);
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.modeButtons.length - 1;

      const nextButton = this.modeButtons[nextIndex];
      nextButton?.focus();
      if (nextButton && nextIndex !== currentIndex) {
        this.setMode(nextButton.dataset.purchaseModeButton, true);
      }
    }

    updateVariant(updateUrl, changedOptionIndex = null) {
      if (!this.variants.length || !this.variantInput) return;

      const selectedOptions = this.variantSelects.map((select) => select.value);
      let variant = this.findExactVariant(selectedOptions);

      if (changedOptionIndex !== null && !variant?.available) {
        variant = this.findBestAvailableVariant(selectedOptions, changedOptionIndex) || variant;
      }

      variant ||= this.variants.find((entry) => entry.available) || this.variants[0];
      if (!variant) return;

      this.variantSelects.forEach((select, index) => {
        if (variant.options[index] !== undefined) select.value = variant.options[index];
      });
      this.updateOptionAvailability(variant.options);
      this.variantInput.value = variant.id;
      if (this.price) this.price.innerHTML = variant.price_html;

      if (this.comparePrice) {
        const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);
        this.comparePrice.hidden = !hasComparePrice;
        this.comparePrice.innerHTML = hasComparePrice ? variant.compare_at_price_html : '';
      }

      if (this.availability) {
        this.availability.textContent = variant.available ? 'In stock' : 'Out of stock';
        this.availability.dataset.available = String(variant.available);
      }

      if (this.quantityInput) {
        this.quantityInput.min = String(variant.quantity_min || 1);
        this.quantityInput.step = String(variant.quantity_increment || 1);
        if (variant.quantity_max) {
          this.quantityInput.max = String(variant.quantity_max);
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.normalizeQuantity();
      }

      if (this.submitButton && this.submitText) {
        this.submitButton.disabled = !variant.available;
        this.submitButton.setAttribute('aria-disabled', String(!variant.available));
        this.updateSubmitLabel(variant.available);
      }

      if (variant.featured_media_id) {
        const thumb = document.querySelector(`[data-gallery-thumb][data-media-id="${variant.featured_media_id}"]`);
        thumb?.click();
      }

      if (!updateUrl) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    }

    quantityRules() {
      if (!this.quantityInput) return null;

      return {
        min: Number(this.quantityInput.min) || 1,
        max: this.quantityInput.max ? Number(this.quantityInput.max) : Infinity,
        step: Number(this.quantityInput.step) || 1,
      };
    }

    normalizeQuantity() {
      const rules = this.quantityRules();
      if (!rules) return;

      const rawValue = Number(this.quantityInput.value);
      const safeValue = Number.isFinite(rawValue) ? rawValue : rules.min;
      const steppedValue = rules.min + Math.round((safeValue - rules.min) / rules.step) * rules.step;
      const nextValue = Math.min(rules.max, Math.max(rules.min, steppedValue));
      this.quantityInput.value = String(nextValue);
      this.updateQuantityButtons(nextValue, rules);
    }

    adjustQuantity(direction) {
      const rules = this.quantityRules();
      if (!rules) return;

      const currentValue = Number(this.quantityInput.value) || rules.min;
      const nextValue = Math.min(rules.max, Math.max(rules.min, currentValue + direction * rules.step));
      this.quantityInput.value = String(nextValue);
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateQuantityButtons(value, rules) {
      if (this.quantityMinus) this.quantityMinus.disabled = value <= rules.min;
      if (this.quantityPlus) this.quantityPlus.disabled = value >= rules.max;
    }

    findExactVariant(selectedOptions) {
      return this.variants.find((variant) =>
        variant.options.every((value, index) => value === selectedOptions[index])
      );
    }

    findBestAvailableVariant(selectedOptions, changedOptionIndex) {
      const changedValue = selectedOptions[changedOptionIndex];
      const candidates = this.variants.filter(
        (variant) => variant.available && variant.options[changedOptionIndex] === changedValue
      );

      return candidates.sort((left, right) => {
        const score = (variant) =>
          variant.options.reduce((total, value, index) => {
            if (index === changedOptionIndex) return total;
            return total + Number(value === selectedOptions[index]);
          }, 0);

        return score(right) - score(left);
      })[0];
    }

    updateOptionAvailability(selectedOptions) {
      this.variantSelects.forEach((select, index) => {
        Array.from(select.options).forEach((option) => {
          const available = this.availableVariants.some((variant) => {
            if (!variant.available || variant.options[index] !== option.value) return false;

            return variant.options.slice(0, index).every((value, optionIndex) => {
              return value === selectedOptions[optionIndex];
            });
          });
          option.hidden = !available;
          option.disabled = !available;
        });
      });
    }

    updateSubmitLabel(variantAvailable = true) {
      if (!this.submitText) return;
      if (!variantAvailable) {
        this.submitText.textContent = 'Out of stock';
        return;
      }
      this.submitText.textContent = this.assemblyCheckbox?.checked
        ? 'Add kit + assembly service'
        : 'Add complete kit';
    }

    updateAssemblyState() {
      if (!this.assemblyCheckbox || !this.assemblyDetails) return;

      const enabled = this.assemblyCheckbox.checked && !this.assemblyCheckbox.disabled;
      this.assemblyDetails.hidden = !enabled;
      this.assemblyMeasurement?.toggleAttribute('required', enabled);
      this.assemblyFit?.toggleAttribute('required', enabled);

      if (enabled && this.quantityInput) {
        this.quantityMaxBeforeAssembly = this.quantityInput.getAttribute('max') || '';
        this.quantityInput.value = '1';
        this.quantityInput.max = '1';
      } else if (this.quantityInput) {
        if (this.quantityMaxBeforeAssembly) this.quantityInput.max = this.quantityMaxBeforeAssembly;
        else this.quantityInput.removeAttribute('max');
      }

      this.normalizeQuantity();
      this.updateSubmitLabel(!this.submitButton?.disabled);
      this.clearAssemblyError();
    }

    updateMeasurementUnit() {
      if (!this.assemblyMeasurement || !this.assemblyUnit) return;
      const usesInches = this.assemblyUnit.value === 'in';
      const unitKey = usesInches ? 'In' : 'Cm';
      const fallback = this.assemblyKind === 'necklace'
        ? { min: usesInches ? 12 : 30, max: usesInches ? 48 : 120, placeholder: usesInches ? 18 : 45 }
        : { min: usesInches ? 3 : 8, max: usesInches ? 14 : 35, placeholder: usesInches ? 6.5 : 16.5 };
      const minimum = Number(this.assemblyService?.dataset[`min${unitKey}`] || fallback.min);
      const maximum = Number(this.assemblyService?.dataset[`max${unitKey}`] || fallback.max);
      const placeholder = Number(this.assemblyService?.dataset[`placeholder${unitKey}`] || fallback.placeholder);
      this.assemblyMeasurement.min = String(minimum);
      this.assemblyMeasurement.max = String(maximum);
      this.assemblyMeasurement.placeholder = String(placeholder);
      this.clearAssemblyError();
    }

    clearAssemblyError() {
      if (!this.assemblyError) return;
      this.assemblyError.hidden = true;
      this.assemblyError.textContent = '';
      this.assemblyMeasurement?.removeAttribute('aria-invalid');
    }

    showAssemblyError(message) {
      if (this.assemblyError) {
        this.assemblyError.textContent = message;
        this.assemblyError.hidden = false;
      }
      this.assemblyMeasurement?.setAttribute('aria-invalid', 'true');
      this.assemblyMeasurement?.focus();
    }

    validateAssembly() {
      const measurement = Number(this.assemblyMeasurement?.value);
      const unit = this.assemblyUnit?.value === 'in' ? 'in' : 'cm';
      const unitKey = unit === 'in' ? 'In' : 'Cm';
      const fallbackMinimum = this.assemblyKind === 'necklace' ? (unit === 'in' ? 12 : 30) : (unit === 'in' ? 3 : 8);
      const fallbackMaximum = this.assemblyKind === 'necklace' ? (unit === 'in' ? 48 : 120) : (unit === 'in' ? 14 : 35);
      const minimum = Number(this.assemblyService?.dataset[`min${unitKey}`] || fallbackMinimum);
      const maximum = Number(this.assemblyService?.dataset[`max${unitKey}`] || fallbackMaximum);
      const measurementName = this.assemblyKind === 'necklace' ? 'finished necklace length' : 'wrist circumference';

      if (!Number.isFinite(measurement) || measurement < minimum || measurement > maximum) {
        this.showAssemblyError(
          `Enter a ${measurementName} between ${minimum} and ${maximum} ${unit}.`
        );
        return null;
      }

      this.clearAssemblyError();
      return {
        measurement: String(Math.round(measurement * 10) / 10),
        unit,
        kind: this.assemblyKind,
        fit: this.assemblyFit?.value || 'Comfort',
        notes: this.assemblyNotes?.value.trim() || '',
      };
    }

    createAssemblyGroupId() {
      if (window.crypto?.randomUUID) return `assembly-${window.crypto.randomUUID()}`;
      return `assembly-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    setAssemblySubmitting(submitting) {
      if (!this.submitButton) return;
      this.submitButton.toggleAttribute('disabled', submitting);
      this.submitButton.setAttribute('aria-disabled', String(submitting));
      this.submitButton.classList.toggle('loading', submitting);
      this.submitButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !submitting);
    }

    showProductFormError(message = '') {
      const wrapper = this.finishedForm?.querySelector('.product-form__error-message-wrapper');
      const output = wrapper?.querySelector('.product-form__error-message');
      if (!wrapper || !output) return;
      wrapper.toggleAttribute('hidden', !message);
      output.textContent = message;
    }

    async handleAssemblySubmit(event) {
      if (!this.assemblyCheckbox?.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.submitButton?.disabled) return;

      const details = this.validateAssembly();
      if (!details) return;

      const kitVariantId = Number(this.variantInput?.value || 0);
      const serviceVariantId = Number(this.assemblyService?.dataset.serviceVariantId || 0);
      const serviceAvailable = this.assemblyService?.dataset.serviceAvailable === 'true';
      if (!kitVariantId || !serviceVariantId || !serviceAvailable) {
        this.showProductFormError('Assembly service is temporarily unavailable. Please add the kit without assembly.');
        return;
      }

      const groupId = this.createAssemblyGroupId();
      const kitProperties = {
        '_purchase_mode': 'Complete Design Kit',
        '_assembly_group': groupId,
        '_assembly_role': 'kit',
        '_assembly_kind': details.kind,
        'Assembly service': 'Yes',
      };
      if (details.kind === 'necklace') {
        kitProperties['Finished necklace length'] = `${details.measurement} ${details.unit}`;
      } else {
        kitProperties['Wrist circumference'] = `${details.measurement} ${details.unit}`;
        kitProperties['Preferred fit'] = details.fit;
      }
      if (details.notes) kitProperties['Assembly notes'] = details.notes;

      const items = [
        {
          id: serviceVariantId,
          quantity: 1,
          properties: {
            '_assembly_group': groupId,
            '_assembly_role': 'service',
            '_assembly_kind': details.kind,
            '_assembly_for': this.kitProductTitle,
            '_assembly_parent_variant_id': String(kitVariantId),
          },
        },
        {
          id: kitVariantId,
          quantity: 1,
          properties: kitProperties,
        },
      ];

      const sections = this.cart?.getSectionsToRender?.().map((section) => section.id) || [];
      this.showProductFormError();
      this.setAssemblySubmitting(true);
      this.cart?.setActiveElement?.(document.activeElement);

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections,
            sections_url: window.location.pathname,
          }),
        });
        const state = await response.json();
        if (!response.ok || state.status) {
          throw new Error(state.description || state.message || 'Unable to add the kit and assembly service.');
        }

        const addedItems = Array.isArray(state.items) ? state.items : [];
        const kitItem = addedItems.find((item) => Number(item.id) === kitVariantId) || addedItems[0];
        const cartState = kitItem ? { ...state, ...kitItem, sections: state.sections } : state;

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: kitVariantId,
            cartData: cartState,
          });
        }

        if (this.cart?.renderContents && kitItem) {
          this.cart.renderContents(cartState);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        this.showProductFormError(error.message || 'Unable to add the kit and assembly service.');
      } finally {
        this.setAssemblySubmitting(false);
        this.updateVariant(false);
      }
    }

    syncHeaderMode(mode) {
      const isFinished = mode === 'finished';
      const topLevelItems = document.querySelectorAll(
        '[data-celestria-nav-root], .header__inline-menu > .list-menu > li > a, .header__inline-menu > .list-menu > li > header-menu > details > summary, .menu-drawer__navigation > .menu-drawer__menu > li > a, .menu-drawer__navigation > .menu-drawer__menu > li > details > summary'
      );

      topLevelItems.forEach((item) => {
        const label = item.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
        const navRoot = item.dataset.celestriaNavRoot;
        const readyToWear =
          navRoot === 'ready-to-wear' ||
          label.includes('ready-to-wear') ||
          label.includes('ready to wear') ||
          label.includes('design kit');
        const inspirations = navRoot === 'inspirations' || label === 'the inspirations' || label === 'inspirations';
        if (!readyToWear && !inspirations) return;

        const active = readyToWear ? isFinished : !isFinished;
        item.classList.toggle('menu-drawer__menu-item--active', active);
        item.classList.toggle('header-mobile-dropdown__link--active', active);
        item.querySelector(':scope > span')?.classList.toggle('header__active-menu-item', active);
        if (item.matches('a')) {
          if (active) item.setAttribute('aria-current', 'page');
          else item.removeAttribute('aria-current');
        }
      });
    }
  }

  if (!customElements.get('product-purchase-modes')) {
    customElements.define('product-purchase-modes', ProductPurchaseModes);
  }
})();
